import * as i0 from '@angular/core';
import { inject, ChangeDetectorRef, Directive, InjectionToken, Optional, Host, booleanAttribute, forwardRef, Input, ElementRef, Renderer2, EventEmitter, Output, HostListener, signal, NgModule, reflectComponentType, Injectable } from '@angular/core';
import { pluckFromArray, isPresent, injectElement, hasChanged, deferFn, booleanToOptionalAttributeString, reactiveAttributeBinding, getLanguageCode } from '@odx/angular/utils';
import { merge, EMPTY, tap, Subject, shareReplay, map, distinctUntilChanged, switchMap, NEVER, debounceTime, Observable, startWith, of, combineLatest, filter } from 'rxjs';
import { CommonModule, DOCUMENT } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { fromEvents } from '@odx/angular/rxjs';

function controllerChanges(controllers) {
    const changes$ = pluckFromArray(controllers.filter(isPresent), 'change$');
    return changes$.length > 0 ? merge(...changes$) : EMPTY;
}
/**
 * Sets up change detection for the provided controllers within the context of an Angular component or directive.
 * It listens for changes from the specified controllers and triggers Angular's change detection mechanism in response.
 *
 * @param {...Array<Controller | undefined | null>} controllers - The controllers to monitor for changes.
 * @returns {Observable<void>} An observable that emits whenever a change occurs in any of the provided controllers.
 */
function detectControllerChanges(...controllers) {
    const changeDetector = inject(ChangeDetectorRef);
    return controllerChanges(controllers).pipe(tap(() => changeDetector.markForCheck()));
}
/**
 * An abstract base class representing a controller that can notify observers of changes in its state.
 * It provides lifecycle hooks for ngOnChanges and ngOnDestroy to manage its internal change observable.
 */
class Controller {
    constructor() {
        this.change$$ = new Subject();
        /**
         * Represents an observable stream of changes.
         * @readonly
         */
        this.change$ = this.change$$.pipe(shareReplay({ bufferSize: 1, refCount: true }));
    }
    /**
     * Sets up a controller instance to emit change events and trigger Angular change detection whenever
     * the controller's state changes. It is a utility method that can be used to initialize controllers
     * that are managed outside of Angular's DI system.
     *
     * @static
     * @param {Controller | null} controller - The controller instance to set up.
     * @param {ChangeDetectorRef} changeDetector - The Angular change detector reference to trigger change detection.
     * @returns {Controller | null} The same controller instance for chaining or null if the controller is not provided.
     */
    static setup(controller, changeDetector) {
        controllerChanges([controller])
            .pipe(tap(() => changeDetector.markForCheck()))
            .subscribe();
        return controller;
    }
    ngOnChanges() {
        this.triggerControllerChange();
    }
    ngOnDestroy() {
        this.change$$.complete();
    }
    triggerControllerChange() {
        this.change$$.next();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: Controller, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: Controller, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: Controller, decorators: [{
            type: Directive
        }] });

const DISABLED_CONTROLLER = new InjectionToken('@odx/angular::DisabledWatchedController');
const DISABLED_INTERNAL_CONTROLLER = new InjectionToken('@odx/angular::DisabledController');
/**
 * Directive that provides hierarchical disabled state management for Angular components or directives.
 * It allows for a disabled state to be inherited from parent components, facilitating the propagation
 * of disabled states down component trees. This is particularly useful in forms or complex component
 * structures where nested components need to respond to disabled states dynamically.
 *
 * The `DisabledController` works by injecting itself or a parent `DisabledController` instance to determine
 * the effective disabled state, taking into account both its own state and that of its parents.
 *
 * Usage:
 * Apply the `disabled` attribute, potentially bound to a component property, to use this directive.
 * The directive can be automatically provided to the component's children by calling the `connect`
 * static method in the component's providers array.
 *
 * @example
 * ```ts
 * @Component({
 *   selector: 'app-custom-input',
 *   templateUrl: './custom-input.component.html',
 *   providers: [DisabledController.connect()],
 *   standalone: true,
 * })
 * export class CustomInputComponent {
 *   constructor(private disabledCtrl: DisabledController) {}
 * }
 * ```
 * ```html
 * <input [disabled]="disabledCtrl.disabled" />
 * ```
 */
class DisabledController extends Controller {
    constructor() {
        super(...arguments);
        this.parentController = inject(DISABLED_CONTROLLER, { optional: true, skipSelf: true });
        this.isDisabled = false;
        /**
         * A reactive representation of the current effective disabled state, considering both
         * the controller's own state and that of its parent controllers.
         *
         * @emits {boolean} - The current disabled state.
         */
        this.isDisabled$ = this.change$.pipe(map(() => this.disabled), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
    }
    /**
     * Sets the disabled state of the current controller. If the state changes, updates the internal
     * flag.
     *
     * @param {boolean} value - The new disabled state to be applied.
     */
    set disabled(value) {
        this.isDisabled = value;
    }
    /**
     * Gets a value indicating whether the controller is disabled.
     *
     * @returns {boolean} - `true` if the controller is disabled, `false` otherwise.
     */
    get disabled() {
        return this.isDisabled || !!this.parentController?.disabled;
    }
    /**
     * Retrieves an instance of the `DisabledController` class from the dependency injection container.
     * If the `DisabledController` is not registered in the container, it returns `null`.
     *
     * @static
     * @returns {DisabledController | null} An instance of the `DisabledController` class or `null` if not found.
     */
    static inject() {
        return inject(DISABLED_CONTROLLER, { optional: true });
    }
    /**
     * Static method to facilitate the injection of the `DisabledController` within Angular's DI system.
     * This method provides a way to connect a component with its closest `DisabledController` parent,
     * allowing for disabled state inheritance.
     *
     * @static
     * @returns {Provider[]} An array of providers to enable disabled state management within a component.
     */
    static connect() {
        return [
            {
                provide: DISABLED_INTERNAL_CONTROLLER,
                useFactory: (hostController) => hostController ?? new DisabledController(),
                deps: [[new Optional(), new Host(), DisabledController]],
            },
            {
                provide: DISABLED_CONTROLLER,
                useFactory: Controller.setup,
                deps: [[new Optional(), new Host(), DISABLED_INTERNAL_CONTROLLER], ChangeDetectorRef],
            },
        ];
    }
    /**
     * Sets the disabled state of the current controller and triggers change detection if the state changes.
     *
     * @param {boolean} state - The new disabled state to be applied.
     */
    setDisabledState(state) {
        if (this.disabled !== state) {
            this.disabled = state;
            this.ngOnChanges();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DisabledController, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "18.2.13", type: DisabledController, isStandalone: true, selector: "[disabled]", inputs: { disabled: ["disabled", "disabled", booleanAttribute] }, providers: [
            {
                provide: DISABLED_INTERNAL_CONTROLLER,
                useExisting: forwardRef(() => DisabledController),
            },
        ], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DisabledController, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[disabled]',
                    providers: [
                        {
                            provide: DISABLED_INTERNAL_CONTROLLER,
                            useExisting: forwardRef(() => DisabledController),
                        },
                    ],
                }]
        }], propDecorators: { disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }] } });

const ELEMENTS_WITH_READONLY_ATTRIBUTE = {
    input: true,
    textarea: true,
    select: true,
};
const READONLY_CONTROLLER = new InjectionToken('@odx/angular::ReadonlyWatchedController');
const READONLY_INTERNAL_CONTROLLER = new InjectionToken('@odx/angular::ReadonlyController');
/**
 * Directive to manage and propagate the readonly state within Angular components or directives,
 * particularly useful for form elements like input, textarea, and select. It supports hierarchical
 * readonly state management, allowing a parent component's readonly state to be inherited by its children.
 *
 * The directive leverages Angular's dependency injection system to access parent readonly controllers,
 * enabling a cascading readonly state. This is beneficial in scenarios where a form or a part of it
 * should become readonly based on certain conditions.
 *
 * Usage:
 * Apply the `readonly` attribute, potentially bound to a component property, to use this directive.
 * The directive can automatically provide itself to the component's children by calling the `connect`
 * static method in the component's providers array.
 *
 * @example
 * ```ts
 * @Component({
 *   selector: 'app-custom-input',
 *   templateUrl: './custom-input.component.html',
 *   providers: [ReadonlyController.connect()],
 *   standalone: true,
 * })
 * export class CustomInputComponent {
 *   constructor(private readonlyCtrl: ReadonlyController) {}
 * }
 * ```
 *
 * Template for CustomInputComponent with conditional readonly state:
 * ```html
 * <input [readonly]="readonlyCtrl.readonly" />
 * ```
 */
class ReadonlyController extends Controller {
    /**
     * Sets the readonly state of the current controller. If the state changes, updates the internal
     * flag that controls the rendering of the `readonly` attribute in the host element.
     *
     * @param {boolean} value - The new readonly state to be applied.
     */
    set readonly(value) {
        if (this.isReadonly === value)
            return;
        this.isReadonly = value;
        this.hasReadonlyAttribute = this.isReadonly && ELEMENTS_WITH_READONLY_ATTRIBUTE[this.nodeName];
    }
    get readonly() {
        return this.isReadonly || !!this.parentController?.readonly;
    }
    constructor(element) {
        super();
        this.parentController = inject(READONLY_CONTROLLER, { optional: true, skipSelf: true });
        this.isReadonly = false;
        this.hasReadonlyAttribute = false;
        /**
         * An observable that emits the current value of the `isReadonly` property whenever it changes.
         * The emitted values are distinct and replayed to new subscribers.
         *
         * @emits {boolean} The current readonly state of the controller.
         */
        this.isReadonly$ = this.change$.pipe(map(() => this.isReadonly), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
        this.nodeName = element?.nativeElement.nodeName.toLowerCase() ?? '';
    }
    /**
     * Returns an instance of the ReadonlyController or null if it cannot be found.
     *
     * @static
     * @returns {ReadonlyController | null} An instance of the ReadonlyController or null.
     */
    static inject() {
        return inject(READONLY_CONTROLLER, { optional: true });
    }
    /**
     * Static method to facilitate the injection of the `ReadonlyController` within Angular's DI system.
     * This method provides a way to connect a component with its closest `ReadonlyController` parent,
     * allowing for readonly state inheritance.
     *
     * @static
     * @returns {Provider} An array of providers to enable readonly state management within a component.
     */
    static connect() {
        return [
            {
                provide: READONLY_INTERNAL_CONTROLLER,
                useFactory: (hostController, elementRef) => hostController ?? new ReadonlyController(elementRef),
                deps: [
                    [new Optional(), new Host(), ReadonlyController],
                    [new Optional(), ElementRef],
                ],
            },
            {
                provide: READONLY_CONTROLLER,
                useFactory: Controller.setup,
                deps: [[new Optional(), new Host(), READONLY_INTERNAL_CONTROLLER], ChangeDetectorRef],
            },
        ];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ReadonlyController, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "18.2.13", type: ReadonlyController, isStandalone: true, selector: "[readonly]", inputs: { readonly: ["readonly", "readonly", booleanAttribute] }, host: { properties: { "attr.readonly": "hasReadonlyAttribute || null" } }, providers: [
            {
                provide: READONLY_INTERNAL_CONTROLLER,
                useExisting: forwardRef(() => ReadonlyController),
            },
        ], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ReadonlyController, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[readonly]',
                    providers: [
                        {
                            provide: READONLY_INTERNAL_CONTROLLER,
                            useExisting: forwardRef(() => ReadonlyController),
                        },
                    ],
                    host: {
                        '[attr.readonly]': 'hasReadonlyAttribute || null',
                    },
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { readonly: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }] } });

/**
 * Directive to detect clicks outside of an element. When active, it listens for clicks outside
 * the host element and emits an event through the `odxClickOutside` EventEmitter. This is useful
 * for handling UI logic that depends on outside clicks, such as closing a dropdown or modal
 * when the user clicks elsewhere on the page.
 *
 * Apply `odxClickOutside` to an element and bind an event handler to react to outside clicks.
 * Optionally, use `[odxClickOutsideActive]="condition"` to enable or disable the detection dynamically.
 *
 * @example
 * ```html
 * <div
 *   odxClickOutside
 *   (odxClickOutside)="handleClickOutside()"
 *   [odxClickOutsideActive]="isDropdownOpen">
 *   <!-- Dropdown content -->
 * </div>
 * ```
 */
class ClickOutsideDirective {
    constructor() {
        this.element = injectElement();
        this.renderer = inject(Renderer2);
        this.onOutsideClick$ = this.registerEventHandler('mousedown').pipe(switchMap((event) => {
            if (!this.odxClickOutsideActive)
                return NEVER;
            const hostElement = this.element.nativeElement;
            const targetElement = event.target;
            const clickedOutside = hostElement.isConnected && targetElement.isConnected && !hostElement.contains(targetElement);
            return this.registerEventHandler('mouseup').pipe(debounceTime(0), tap(() => clickedOutside && this.odxClickOutside.emit(event)));
        }));
        /**
         * Controls whether the outside click detection is active. When `true`, the directive listens for outside clicks.
         *
         * @type {boolean}
         * @default true
         */
        this.odxClickOutsideActive = true;
        /**
         * EventEmitter that emits the native Event object when an outside click is detected.
         *
         * @emits {Event}
         */
        this.odxClickOutside = new EventEmitter();
    }
    ngOnInit() {
        if (this.odxClickOutsideActive) {
            this.registerHandler();
        }
    }
    ngOnChanges(changes) {
        if (hasChanged(changes, 'odxClickOutsideActive', true)) {
            if (changes.odxClickOutsideActive?.currentValue) {
                this.registerHandler();
            }
            else {
                this.destroy();
            }
        }
    }
    ngOnDestroy() {
        this.destroy();
    }
    destroy() {
        if (this.listener) {
            this.listener.unsubscribe();
            this.listener = undefined;
        }
    }
    registerHandler() {
        deferFn(() => {
            this.listener = this.onOutsideClick$.subscribe();
        });
    }
    registerEventHandler(eventName) {
        return new Observable((subscriber) => {
            const listener = this.renderer.listen('document', eventName, (event) => {
                subscriber.next(event);
            });
            subscriber.add(listener);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ClickOutsideDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "18.2.13", type: ClickOutsideDirective, isStandalone: true, selector: "[odxClickOutside]", inputs: { odxClickOutsideActive: ["odxClickOutsideActive", "odxClickOutsideActive", booleanAttribute] }, outputs: { odxClickOutside: "odxClickOutside" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ClickOutsideDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[odxClickOutside]',
                }]
        }], propDecorators: { odxClickOutsideActive: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], odxClickOutside: [{
                type: Output
            }] } });

/**
 * A directive to manage focus within components. It emits events when the host component is focused,
 * and dynamically updates the `tabindex` attribute to control focusability. This directive is particularly
 * useful for custom components that require explicit focus management, such as custom input components
 * or complex interactive components.
 *
 * The directive ensures that the component can be focused by setting `tabindex="0"`, making it reachable
 * via keyboard navigation. When the component gains focus (`focusin`), it becomes non-focusable (`tabindex="-1"`)
 * to manage focus delegation internally. It restores its focusability (`tabindex="0"`) when losing focus (`focusout`),
 * allowing it to be focused again in the future.
 */
class DelegateFocusDirective {
    constructor() {
        this.tabindex = 0;
        /**
         * Emits an event when the element is focused.
         */
        this.focused = new EventEmitter();
    }
    handleFocus() {
        this.focused.next();
    }
    handleFocusIn() {
        this.tabindex = -1;
    }
    handleFocusOut() {
        this.tabindex = 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DelegateFocusDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: DelegateFocusDirective, isStandalone: true, outputs: { focused: "focused" }, host: { listeners: { "focus": "handleFocus()", "focusin": "handleFocusIn()", "focusout": "handleFocusOut()" }, properties: { "attr.tabindex": "tabindex" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DelegateFocusDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    host: {
                        '[attr.tabindex]': 'tabindex',
                    },
                }]
        }], propDecorators: { focused: [{
                type: Output
            }], handleFocus: [{
                type: HostListener,
                args: ['focus']
            }], handleFocusIn: [{
                type: HostListener,
                args: ['focusin']
            }], handleFocusOut: [{
                type: HostListener,
                args: ['focusout']
            }] } });

const ELEMENTS_WITH_DISABLED_ATTRIBUTE = {
    button: true,
    fieldset: true,
    input: true,
    optgroup: true,
    option: true,
    select: true,
    textarea: true,
};
class WithDisabledState {
    get isDisabled() {
        return !!this.disabledController?.disabled;
    }
    get ariaDisabled() {
        return booleanToOptionalAttributeString(this.hasAriaRole && this.isDisabled);
    }
    constructor(element) {
        this.disabledController = DisabledController.inject();
        this.hasDisabledAttribute = !!ELEMENTS_WITH_DISABLED_ATTRIBUTE[element.nativeElement.nodeName.toLowerCase() ?? ''];
        this.hasAriaRole = element.nativeElement.hasAttribute('role');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WithDisabledState, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: WithDisabledState, isStandalone: true, host: { properties: { "class.is-disabled": "isDisabled", "attr.aria-disabled": "ariaDisabled", "attr.disabled": "hasDisabledAttribute && isDisabled || null" } }, providers: [DisabledController.connect()], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WithDisabledState, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    providers: [DisabledController.connect()],
                    host: {
                        '[class.is-disabled]': 'isDisabled',
                        '[attr.aria-disabled]': 'ariaDisabled',
                        '[attr.disabled]': 'hasDisabledAttribute && isDisabled || null',
                    },
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class WithTabIndex {
    constructor() {
        this.isDisabled$ = DisabledController.inject()?.isDisabled$.pipe(startWith(false)) ?? of(false);
        this.tabindex = signal(0);
        this.tabindex$ = combineLatest([toObservable(this.tabindex), this.isDisabled$]).pipe(map(([tabindex, isDisabled]) => (isDisabled ? -1 : tabindex)), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
        reactiveAttributeBinding(this.tabindex$, 'tabindex');
    }
    setTabindex(value) {
        this.tabindex.set(value ?? 0);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WithTabIndex, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: WithTabIndex, isStandalone: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WithTabIndex, decorators: [{
            type: Directive,
            args: [{ standalone: true }]
        }], ctorParameters: () => [] });

const controllers = [DisabledController, ReadonlyController];
class CoreModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: CoreModule, imports: [DisabledController, ReadonlyController, ClickOutsideDirective], exports: [CommonModule, DisabledController, ReadonlyController, ClickOutsideDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CoreModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...controllers, ClickOutsideDirective],
                    exports: [CommonModule, ...controllers, ClickOutsideDirective],
                }]
        }] });

const Environment = {
    DEV: 'dev',
    STAGE: 'stage',
    PROD: 'prod'
};

/**
 * A service that wraps window and document functionalities, providing Angular-friendly ways to interact with the browser's window object,
 * manage the document and its title, listen to online/offline events, and perform other browser-related operations.
 */
class WindowRef {
    constructor() {
        this.title = inject(Title);
        /**
         * Accesses the document object, ensuring Angular compatibility and potential SSR friendliness.
         *
         * @type {Document}
         */
        this.document = inject(DOCUMENT);
        /**
         * Retrieves the native window object, with a fallback to the global window if necessary.
         * This approach helps ensure compatibility with Angular's SSR features.
         *
         * @type {Window}
         */
        this.nativeWindow = this.document.defaultView ?? window;
        /**
         * An observable that emits the online status of the browser, updating when the browser transitions
         * between online and offline states.
         *
         * @emits {boolean} True if the browser is online; otherwise, false.
         */
        this.isOnline$ = fromEvents(this.nativeWindow, 'offline', 'online').pipe(startWith(null), map(() => this.isOnline()), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
        /**
         * An observable that emits the current page title upon navigation ends,
         * useful for updating document titles in SPA applications.
         *
         * @emits {string} The current page title.
         */
        this.pageTitle$ = (inject(Router, { optional: true })?.events ?? EMPTY).pipe(filter((event) => event instanceof NavigationEnd), startWith(null), debounceTime(0), map(() => this.title.getTitle()), shareReplay({ bufferSize: 1, refCount: true }));
    }
    /**
     * Gets the location object of the window.
     *
     * @returns {Location} The location object of the window.
     */
    get location() {
        return this.nativeWindow.location;
    }
    /**
     * Determines if the browser is currently online.
     *
     * @returns {boolean} True if the browser is online; otherwise, false.
     */
    isOnline() {
        return this.nativeWindow.navigator.onLine;
    }
    /**
     * Retrieves the browser's current language setting, falling back through several browser-specific properties.
     *
     * @returns {string} The current language setting of the browser.
     */
    getLanguage() {
        const { language, languages, userLanguage } = this.nativeWindow.navigator;
        return languages?.[0] ?? userLanguage ?? language;
    }
    /**
     * Extracts the language code from the browser's current language setting.
     *
     * @returns {string} The language code part of the browser's language setting.
     */
    getLanguageCode() {
        return getLanguageCode(this.getLanguage());
    }
    /**
     * Returns the origin of the current window location.
     *
     * @returns The origin of the current window location.
     */
    getOrigin() {
        return this.location.origin;
    }
    /**
     * Opens a new browser tab or window and navigates to the specified URL.
     *
     * @param {string | URL} [url] - The URL to open.
     * @param {string} [target] - The target window or tab.
     * @param {string} [features] - String that specifies the window features.
     * @returns {WindowProxy | null} A reference to the newly opened window or null if the call failed.
     */
    openPage(url, target, features) {
        return this.nativeWindow.open(url, target, features);
    }
    /**
     * Reloads the current page.
     */
    reloadPage() {
        return this.location.reload();
    }
    /**
     * Queries the DOM for an element associated with a specific Angular component type.
     *
     * @template T The component type to query for.
     * @param {Type<T>} type - The Angular component type.
     * @returns {HTMLElement | null} The HTML element associated with the component, or null if not found.
     */
    queryByComponent(type) {
        const componentType = reflectComponentType(type);
        return componentType ? this.nativeWindow.document.querySelector(componentType.selector) : null;
    }
    /**
     * Retrieves the value of a CSS variable on a specified element.
     *
     * @param {string} name - The name of the CSS variable.
     * @param {HTMLElement} [element=this.document.body] - The element from which to retrieve the CSS variable.
     * @returns {string} The value of the CSS variable.
     */
    getCSSVariable(name, element = this.document.body) {
        return this.nativeWindow.getComputedStyle(element).getPropertyValue(`--${name}`).trim();
    }
    /**
     * Extracts CSS color styles that match a specific filter from the document's stylesheets.
     *
     * @param {string} filter - The filter to apply to CSS variable names.
     * @param {string} [selector=':root'] - The CSS selector to filter by.
     * @returns {Record<string, string>} A record containing the filtered CSS color styles.
     */
    getCSSColorStyles(filter, selector = ':root') {
        const styles = {};
        const sheets = Array.from(this.document.styleSheets).filter(({ href }) => !href || href.startsWith(this.nativeWindow.location.origin));
        for (const sheet of sheets) {
            const rules = sheet.cssRules || sheet.rules;
            for (const rule of Array.from(rules)) {
                if (rule instanceof CSSStyleRule && rule.selectorText === selector) {
                    for (const style of Array.from(rule.style)) {
                        if (style.startsWith('--') && style.includes(filter)) {
                            const key = style.replace('--', '');
                            styles[key] = this.getCSSVariable(key);
                        }
                    }
                }
            }
        }
        return styles;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WindowRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WindowRef, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WindowRef, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const ODX_DEFAULT_IDENTITY_MATCHER = (item1, item2) => item1 === item2;
/**
 * An Angular `InjectionToken` for the `IdentityMatcher` function, allowing for the provision
 * and injection of custom identity matching logic throughout the application. The token is
 * provided with a default factory function that returns the `ODX_DEFAULT_IDENTITY_MATCHER`,
 * which can be overridden with custom logic as needed.
 *
 * @type {InjectionToken<IdentityMatcher<unknown>>}
 *
 * @example
 * ```ts
 * // Providing a custom identity matcher in an Angular module
 * @NgModule({
 *   providers: [
 *     { provide: ODX_IDENTITY_MATCHER, useValue: (item1, item2) => item1.id === item2.id }
 *   ]
 * })
 * class AppModule {}
 *
 * // Injecting and using the custom provided identity matcher
 * constructor(@Inject(ODX_IDENTITY_MATCHER) private identityMatcher: IdentityMatcher<MyType>) {
 *   console.log(this.identityMatcher({ id: 1 }, { id: 1 })); // Depends on the custom logic provided
 * }
 * ```
 */
const ODX_IDENTITY_MATCHER = new InjectionToken('@odx/angular::StringHandler', {
    providedIn: 'root',
    factory: () => ODX_DEFAULT_IDENTITY_MATCHER,
});

const ODX_DEFAULT_STRING_SEARCH_HANDLER = (item, query) => {
    return item.toLocaleLowerCase().includes(query.toLocaleLowerCase());
};
/**
 * An Angular `InjectionToken` for the `StringSearchHandler` function, allowing for the provision
 * and injection of custom string search logic throughout the application. This token is provided with
 * a default factory function that returns the `ODX_DEFAULT_STRING_SEARCH_HANDLER`, which can be overridden
 * with custom logic as needed.
 *
 * @type {InjectionToken<StringSearchHandler>}
 *
 * @example
 * ```ts
 * // Providing a custom string search handler in an Angular module
 * @NgModule({
 *   providers: [
 *     { provide: ODX_STRING_SEARCH_HANDLER, useValue: (item, query) => item.startsWith(query) }
 *   ]
 * })
 * class AppModule {}
 *
 * // Injecting and using the custom provided string search handler
 * constructor(@Inject(ODX_STRING_SEARCH_HANDLER) private searchHandler: StringSearchHandler) {
 *   console.log(this.searchHandler('Angular', 'A')); // Depends on the custom logic provided
 * }
 * ```
 */
const ODX_STRING_SEARCH_HANDLER = new InjectionToken('@odx/angular::StringHandler', {
    providedIn: 'root',
    factory: () => ODX_DEFAULT_STRING_SEARCH_HANDLER,
});

const ODX_DEFAULT_STRINGIFY = (item) => (isPresent(item) ? String(item) : '');
/**
 * An Angular `InjectionToken` for the `StringifyFn` function, allowing the injection of custom
 * stringification logic throughout an application. By default, it is provided with a factory
 * function that returns `ODX_DEFAULT_STRINGIFY`. This setup permits easy customization of
 * how items are converted to strings, facilitating different stringification strategies
 * for various application needs.
 *
 * @type {InjectionToken<StringifyFn<unknown>>}
 *
 * @example
 * ```ts
 * // Providing a custom stringify function in an Angular module
 * @NgModule({
 *   providers: [
 *     { provide: ODX_STRINGIFY, useValue: (item) => JSON.stringify(item) }
 *   ]
 * })
 * class AppModule {}
 *
 * // Injecting and using the custom provided stringify function
 * constructor(@Inject(ODX_STRINGIFY) private stringify: StringifyFn<unknown>) {
 *   console.log(this.stringify({ key: 'value' })); // Outputs: '{"key":"value"}'
 * }
 * ```
 */
const ODX_STRINGIFY = new InjectionToken('@odx/angular::Stringify', {
    providedIn: 'root',
    factory: () => ODX_DEFAULT_STRINGIFY,
});

/**
 * Generated bundle index. Do not edit.
 */

export { ClickOutsideDirective, Controller, CoreModule, DISABLED_CONTROLLER, DISABLED_INTERNAL_CONTROLLER, DelegateFocusDirective, DisabledController, Environment, ODX_IDENTITY_MATCHER, ODX_STRINGIFY, ODX_STRING_SEARCH_HANDLER, READONLY_CONTROLLER, READONLY_INTERNAL_CONTROLLER, ReadonlyController, WindowRef, WithDisabledState, WithTabIndex, detectControllerChanges };
